// Ação pendente de step-up (Parte 14.2 "repete a chamada"): o step-up real é um redirect ao provedor, então a ação a
// repetir é guardada em sessionStorage (chave + carga serializável, NUNCA segredos como senhas) e consumida pela página
// que remonta no `returnTo` após o callback.
export const STEP_UP_PENDING_STORAGE_KEY = 'giyus.stepUp.pending';

export interface PendingStepUp {
  /** Identifica a ação (ex.: `revoke-session`); a página que a executa consome só a sua. */
  readonly key: string;
  /** Carga serializável para repetir a ação; `null` quando a ação precisa ser refeita pelo usuário. */
  readonly payload: unknown;
}

export interface StoredStepUp extends PendingStepUp {
  readonly returnTo: string;
}

export type PendingStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function browserPendingStorage(): PendingStorage | null {
  return typeof sessionStorage === 'undefined' ? null : sessionStorage;
}

export function writePendingStepUp(
  storage: PendingStorage | null | undefined,
  stored: StoredStepUp,
): void {
  try {
    storage?.setItem(STEP_UP_PENDING_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // sem armazenamento a ação simplesmente precisa ser refeita após o retorno
  }
}

export function readPendingStepUp(storage: PendingStorage | null | undefined): StoredStepUp | null {
  try {
    const raw = storage?.getItem(STEP_UP_PENDING_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const value = parsed as { key?: unknown; returnTo?: unknown; payload?: unknown };
    if (typeof value.key !== 'string' || typeof value.returnTo !== 'string') {
      return null;
    }
    return { key: value.key, returnTo: value.returnTo, payload: value.payload ?? null };
  } catch {
    return null;
  }
}

export function clearPendingStepUp(storage: PendingStorage | null | undefined): void {
  try {
    storage?.removeItem(STEP_UP_PENDING_STORAGE_KEY);
  } catch {
    // idem
  }
}

/** Consome (lê e apaga) a ação pendente da chave dada; `undefined` quando não há pendência para ela. */
export function consumePendingStepUp(
  storage: PendingStorage | null | undefined,
  key: string,
): { readonly payload: unknown } | undefined {
  const stored = readPendingStepUp(storage);
  if (stored?.key !== key) {
    return undefined;
  }
  clearPendingStepUp(storage);
  return { payload: stored.payload };
}
